/**
 * React bindings of the shell pieces every mobile app uses (D691): the connection status (gap 8), the safe OTA reload (gap 2) and the foreground trigger
 * they share. Framework-neutral React (no DOM, no React Native import): the app injects React Native's `AppState`.
 */
import { createContext, createElement, useContext, useEffect, useRef, useState } from 'react';
/** Call `listener` each time the app returns to the foreground (`active` after any other state); returns the unsubscribe. */
export function onForeground(source, listener) {
    let last = source.currentState ?? 'active';
    // react-native-web answers `undefined` where the visibility API is missing: nothing to listen to then.
    const subscription = source.addEventListener('change', (state) => {
        const was = last;
        last = state;
        if (state === 'active' && was !== 'active')
            listener();
    });
    return () => subscription?.remove();
}
// ---- connection (gap 8) ---------------------------------------------------------------------------------------------
/** The API's reachability, live. */
export function useNetworkStatus(monitor) {
    const [status, setStatus] = useState(() => monitor.status());
    useEffect(() => {
        setStatus(monitor.status());
        return monitor.subscribe(setStatus);
    }, [monitor]);
    return status;
}
/**
 * Whether the "no connection" banner shows. It appears only after the API has been unreachable for `delayMs` (default 1.2 s), so a single slow call does
 * not flash it, and it goes the moment the API answers. `retry` probes now (the banner's one action).
 */
export function useConnectionBanner(monitor, options = {}) {
    const status = useNetworkStatus(monitor);
    const [visible, setVisible] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const delay = options.delayMs ?? 1_200;
    useEffect(() => {
        if (status === 'online') {
            setVisible(false);
            return undefined;
        }
        const timer = setTimeout(() => setVisible(true), delay);
        return () => clearTimeout(timer);
    }, [status, delay]);
    const retry = () => {
        setRetrying(true);
        void monitor.probe().finally(() => setRetrying(false));
    };
    return { visible: visible && status === 'offline', retrying, retry };
}
// ---- over-the-air updates (gap 2) -----------------------------------------------------------------------------------
const OtaContext = createContext(undefined);
export function OtaProvider({ controller, children }) {
    return createElement(OtaContext.Provider, { value: controller }, children);
}
/** The app's OTA controller, or `undefined` outside an {@link OtaProvider} (a test, the web): every use is then a no-op. */
export function useOta() {
    return useContext(OtaContext);
}
/** Forbid an OTA reload while the calling screen is mounted and `active` (a trip, an offer, a request in flight). */
export function useOtaBlocker(reason, active = true) {
    const ota = useOta();
    useEffect(() => {
        if (ota === undefined || !active)
            return undefined;
        return ota.block(reason);
    }, [ota, reason, active]);
}
/** Renders nothing; forbids an OTA reload while it is mounted with `active` (for a place that has no hook of its own: the app root, a provider). */
export function OtaBlock({ reason, active = true }) {
    useOtaBlocker(reason, active);
    return null;
}
/**
 * Check for a new bundle at the start and each time the app comes back to the foreground (the controller throttles itself), while `enabled`
 * (after the first screen is up). The reload itself happens only at a safe moment - see {@link createOtaController}.
 */
export function useOtaChecks(controller, appState, enabled) {
    const ref = useRef(controller);
    ref.current = controller;
    useEffect(() => {
        if (!enabled)
            return undefined;
        void ref.current?.checkNow();
        return onForeground(appState, () => void ref.current?.checkNow());
    }, [appState, enabled]);
}
//# sourceMappingURL=react-bindings.js.map